import React, { useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControlLabel,
  IconButton,
  InputAdornment,
  LinearProgress,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import LoginIcon from '@mui/icons-material/Login';
import { companiesApi, type CompanyUsage } from '@/features/companies/companies.api';
import { getErrorMessage } from '@/utils/errorHandling';
import { formatCNPJ } from '@/utils/cnpj';
import { formatBytes } from '@/utils/bytes';
import { useNotification } from '@/contexts/NotificationContext';
import { CompanyTableSkeleton } from '@/components/CompanyTableSkeleton';
import { SortableTableCell } from '@/components/SortableTableCell';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { EditCompanyDialog } from '@/components/EditCompanyDialog';
import type { SortOrder } from '@/types/pagination';

type SortKey =
  | 'legalName'
  | 'cnpj'
  | 'status'
  | 'customerPercentage'
  | 'aiPercentage'
  | 'storagePercentage';

function formatPercent(value: number): string {
  return `${value}%`;
}

function progressColor(percentage: number): 'success' | 'warning' | 'error' {
  if (percentage >= 90) return 'error';
  if (percentage >= 70) return 'warning';
  return 'success';
}

interface UsageCellProps {
  used: number;
  limit: number;
  percentage: number;
  unit?: string;
  formatter?: (value: number) => string;
}

const UsageCell: React.FC<UsageCellProps> = ({ used, limit, percentage, unit, formatter }) => {
  const fmt = (v: number) =>
    formatter ? formatter(v) : `${v.toLocaleString('pt-BR')}${unit ? ` ${unit}` : ''}`;

  return (
    <Box sx={{ minWidth: 140 }}>
      <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
        <Typography variant="caption" color="text.secondary">
          {fmt(used)} / {fmt(limit)}
        </Typography>
        <Typography variant="caption" fontWeight={600}>
          {formatPercent(percentage)}
        </Typography>
      </Stack>
      <LinearProgress
        variant="determinate"
        value={Math.min(percentage, 100)}
        color={progressColor(percentage)}
      />
    </Box>
  );
};

type ConfirmAction =
  | { type: 'deactivate'; company: CompanyUsage }
  | { type: 'activate'; company: CompanyUsage };

export const CompaniesListPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showNotification } = useNotification();

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState<SortKey>('legalName');
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');
  const [editTarget, setEditTarget] = useState<CompanyUsage | null>(null);
  const [confirmAction, setConfirmAction] = useState<ConfirmAction | null>(null);

  const { data, isLoading, isFetching, isError, error } = useQuery({
    queryKey: ['admin', 'companies', 'usage', { page, limit, includeDeleted }],
    queryFn: () => companiesApi.listUsage({ page, limit, includeDeleted }),
  });

  const deactivateMutation = useMutation({
    mutationFn: (id: string) => companiesApi.deactivate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'companies'] });
      showNotification('Empresa desativada com sucesso', 'success');
      setConfirmAction(null);
    },
    onError: err => {
      showNotification(getErrorMessage(err, 'Erro ao desativar empresa'), 'error');
    },
  });

  const activateMutation = useMutation({
    mutationFn: (id: string) => companiesApi.activate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'companies'] });
      showNotification('Empresa reativada com sucesso', 'success');
      setConfirmAction(null);
    },
    onError: err => {
      showNotification(getErrorMessage(err, 'Erro ao reativar empresa'), 'error');
    },
  });

  const impersonateMutation = useMutation({
    mutationFn: (companyId: string) => companiesApi.impersonate(companyId),
    onSuccess: ({ code, clientAppUrl }) => {
      const url = `${clientAppUrl.replace(/\/$/, '')}/impersonate?code=${encodeURIComponent(code)}`;
      window.open(url, '_blank', 'noopener,noreferrer');
    },
    onError: err => {
      showNotification(getErrorMessage(err, 'Erro ao impersonar empresa'), 'error');
    },
  });

  const handleSort = (key: SortKey) => {
    if (sortBy === key) {
      setSortOrder(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(key);
      setSortOrder('asc');
    }
  };

  const filteredAndSorted = useMemo<CompanyUsage[]>(() => {
    const rows = (data?.data ?? []).filter(company => company.accountMode !== 'trial');
    const term = search.trim().toLowerCase();
    const termCnpj = term.replace(/[.\-/\s]/g, '').toUpperCase();

    const filtered = rows.filter(c => {
      if (!term) return true;
      const matchName = c.legalName.toLowerCase().includes(term);
      const matchCnpj = termCnpj ? c.cnpj.toUpperCase().includes(termCnpj) : false;
      return matchName || matchCnpj;
    });

    const sorted = [...filtered].sort((a, b) => {
      let cmp = 0;
      switch (sortBy) {
        case 'legalName':
          cmp = a.legalName.localeCompare(b.legalName, 'pt-BR');
          break;
        case 'cnpj':
          cmp = a.cnpj.localeCompare(b.cnpj);
          break;
        case 'status': {
          const sa = a.deletedAt ? 1 : 0;
          const sb = b.deletedAt ? 1 : 0;
          cmp = sa - sb;
          break;
        }
        case 'customerPercentage':
          cmp = a.customerPercentage - b.customerPercentage;
          break;
        case 'aiPercentage':
          cmp = a.aiPercentage - b.aiPercentage;
          break;
        case 'storagePercentage':
          cmp = a.storagePercentage - b.storagePercentage;
          break;
      }
      return sortOrder === 'asc' ? cmp : -cmp;
    });

    return sorted;
  }, [data?.data, search, sortBy, sortOrder]);

  const handleConfirm = async () => {
    if (!confirmAction) return;
    if (confirmAction.type === 'deactivate') {
      await deactivateMutation.mutateAsync(confirmAction.company.id);
    } else {
      await activateMutation.mutateAsync(confirmAction.company.id);
    }
  };

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography variant="h4" component="h1" fontWeight={700}>
            Empresas
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Gestão de tenants e indicadores de uso
          </Typography>
        </Box>

        <Stack direction="row" spacing={2} alignItems="center">
          <FormControlLabel
            control={
              <Switch
                checked={includeDeleted}
                onChange={e => {
                  setIncludeDeleted(e.target.checked);
                  setPage(1);
                }}
              />
            }
            label="Incluir desativadas"
          />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            component={RouterLink}
            to="/companies/new"
          >
            Nova empresa
          </Button>
        </Stack>
      </Stack>

      <Box sx={{ mb: 2 }}>
        <TextField
          fullWidth
          placeholder="Buscar por razão social ou CNPJ..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: search && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setSearch('')}
                    edge="end"
                    aria-label="Limpar busca"
                  >
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ),
            },
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              backgroundColor: 'background.paper',
            },
          }}
        />
      </Box>

      {isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {getErrorMessage(error, 'Erro ao carregar empresas')}
        </Alert>
      )}

      <Paper>
        {isLoading ? (
          <CompanyTableSkeleton rows={Math.min(limit, 8)} />
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <SortableTableCell<SortKey>
                    label="Empresa"
                    sortKey="legalName"
                    currentSortBy={sortBy}
                    currentSortOrder={sortOrder}
                    onSort={handleSort}
                  />
                  <SortableTableCell<SortKey>
                    label="CNPJ"
                    sortKey="cnpj"
                    currentSortBy={sortBy}
                    currentSortOrder={sortOrder}
                    onSort={handleSort}
                  />
                  <SortableTableCell<SortKey>
                    label="Status"
                    sortKey="status"
                    currentSortBy={sortBy}
                    currentSortOrder={sortOrder}
                    onSort={handleSort}
                  />
                  <SortableTableCell<SortKey>
                    label="Clientes"
                    sortKey="customerPercentage"
                    currentSortBy={sortBy}
                    currentSortOrder={sortOrder}
                    onSort={handleSort}
                  />
                  <SortableTableCell<SortKey>
                    label="IA"
                    sortKey="aiPercentage"
                    currentSortBy={sortBy}
                    currentSortOrder={sortOrder}
                    onSort={handleSort}
                  />
                  <SortableTableCell<SortKey>
                    label="Storage"
                    sortKey="storagePercentage"
                    currentSortBy={sortBy}
                    currentSortOrder={sortOrder}
                    onSort={handleSort}
                  />
                  <TableCell align="right">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredAndSorted.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">
                        {search
                          ? 'Nenhuma empresa corresponde à busca nesta página'
                          : 'Nenhuma empresa encontrada'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                {filteredAndSorted.map(c => {
                  const isActive = !c.deletedAt;
                  return (
                    <TableRow
                      key={c.id}
                      hover
                      onClick={() => navigate(`/companies/${c.id}`)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {c.legalName}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {formatCNPJ(c.cnpj)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {isActive ? (
                          <Chip label="Ativa" color="success" size="small" />
                        ) : (
                          <Chip label="Desativada" color="default" size="small" />
                        )}
                      </TableCell>
                      <TableCell>
                        <UsageCell
                          used={c.customerCount}
                          limit={c.customerLimit}
                          percentage={c.customerPercentage}
                        />
                      </TableCell>
                      <TableCell>
                        <UsageCell
                          used={c.aiTokensUsed}
                          limit={c.aiTokenLimit}
                          percentage={c.aiPercentage}
                          unit="tokens"
                        />
                      </TableCell>
                      <TableCell>
                        <UsageCell
                          used={c.storageUsed}
                          limit={c.storageLimit}
                          percentage={c.storagePercentage}
                          formatter={formatBytes}
                        />
                      </TableCell>
                      <TableCell align="right" onClick={e => e.stopPropagation()}>
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                          <Tooltip title="Ver detalhes">
                            <IconButton size="small" onClick={() => navigate(`/companies/${c.id}`)}>
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {isActive && (
                            <Tooltip title="Impersonar empresa">
                              <span>
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => impersonateMutation.mutate(c.id)}
                                  disabled={
                                    impersonateMutation.isPending &&
                                    impersonateMutation.variables === c.id
                                  }
                                >
                                  <LoginIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                          <Tooltip title="Editar empresa">
                            <IconButton size="small" onClick={() => setEditTarget(c)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {isActive ? (
                            <Tooltip title="Desativar empresa">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => setConfirmAction({ type: 'deactivate', company: c })}
                              >
                                <BlockIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          ) : (
                            <Tooltip title="Reativar empresa">
                              <IconButton
                                size="small"
                                color="success"
                                onClick={() => setConfirmAction({ type: 'activate', company: c })}
                              >
                                <CheckCircleIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {data && data.pagination.total > 0 && (
          <TablePagination
            component="div"
            count={data.pagination.total}
            page={page - 1}
            onPageChange={(_, newPage) => setPage(newPage + 1)}
            rowsPerPage={limit}
            onRowsPerPageChange={e => {
              setLimit(parseInt(e.target.value, 10));
              setPage(1);
            }}
            rowsPerPageOptions={[10, 20, 50, 100]}
            labelRowsPerPage="Por página"
            labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
          />
        )}

        {isFetching && !isLoading && (
          <LinearProgress sx={{ borderBottomLeftRadius: 12, borderBottomRightRadius: 12 }} />
        )}
      </Paper>

      <EditCompanyDialog
        open={!!editTarget}
        company={editTarget}
        onClose={() => setEditTarget(null)}
      />

      <ConfirmDialog
        open={!!confirmAction}
        title={confirmAction?.type === 'deactivate' ? 'Desativar empresa?' : 'Reativar empresa?'}
        message={
          confirmAction?.type === 'deactivate' ? (
            <>
              <strong>{confirmAction.company.legalName}</strong> será desativada. Todos os usuários
              perderão acesso em até 15 minutos. Logins novos serão bloqueados imediatamente. Esta
              ação é reversível.
            </>
          ) : confirmAction?.type === 'activate' ? (
            <>
              <strong>{confirmAction.company.legalName}</strong> voltará a estar ativa. Os usuários
              que estavam ativos antes da desativação voltarão a ter acesso.
            </>
          ) : (
            ''
          )
        }
        confirmText={confirmAction?.type === 'deactivate' ? 'Desativar' : 'Reativar'}
        severity={confirmAction?.type === 'deactivate' ? 'error' : 'info'}
        onConfirm={handleConfirm}
        onCancel={() => setConfirmAction(null)}
      />
    </Box>
  );
};
